<template>
  <div>
    <button
      class="text-xs text-left uppercase font-bold border px-2 py-1 transition-all transition-normal no-underline ml-1 text-primary-700 border-primary-500 hover:border-primary-700 hover:bg-primary-100 hover:text-primary-800"
      @click="show"
    >
      {{ disabled ? "View" : "Manage" }}
    </button>
    <portal to="modals">
      <app-modal v-if="isVisible" @close="hide">
        <template #containerless>
          <app-card class="w-full max-w-md mx-auto mt-12">
            <h2 class="text-xl px-6 py-4">
              Manage Recipients
            </h2>
            <div class="px-6 py-4 bg-grey-100">
              <p class="mb-4">
                As the owner of the application,
                <b
                  :class="{
                    'text-negative-500':
                      projectOwner.email === newRecipient.email
                  }"
                  >{{ projectOwner.name }} ({{ projectOwner.email }})</b
                >
                will always receive notifications about this assessment. You can
                add additional recipients to be notified when this assessment is
                submitted or updated.
              </p>
            </div>
            <form @submit.prevent="doSave">
              <table class="w-full table-auto">
                <thead>
                  <tr>
                    <th class="text-left px-6 py-2 border-b">Name</th>
                    <th class="text-left px-6 py-2 border-b">Email</th>
                    <th class="text-center px-6 py-2 border-b">
                      <span class="sr-only">Remove</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(recipient, i) in customRecipients"
                    :key="`recipient-${i}`"
                    :class="{
                      'bg-negative-50': recipient.email === newRecipient.email
                    }"
                  >
                    <td class="text-left px-6 py-2 border-b">
                      {{ recipient.name }}
                    </td>
                    <td class="text-left px-6 py-2 border-b">
                      {{ recipient.email }}
                    </td>
                    <td class="text-right px-6 py-2 border-b">
                      <button
                        v-if="isNotDisabled"
                        @click="removeRecipient(i)"
                        class="text-sm text-negative-700 border border-negative-500 px-2 py-1 hover:bg-negative-100 hover:border-negative-700 hover:text-negative-800 transition-all transition-normal no-underline"
                        type="button"
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-grey-50" v-if="isNotDisabled">
                  <tr>
                    <td class="text-left px-3 py-2">
                      <input
                        ref="nameInput"
                        v-model.trim="newRecipient.name"
                        type="text"
                        class="w-full border px-3 py-1"
                        placeholder="Recipient Name"
                        @keyup.enter="addRecipient"
                        :class="{
                          'text-negative-600':
                            !validRecipientName && newRecipient.name.length > 1
                        }"
                      />
                    </td>
                    <td class="text-left px-3 py-2">
                      <input
                        v-model.trim="newRecipient.email"
                        @keyup.enter="addRecipient"
                        type="email"
                        class="w-full border px-3 py-1"
                        placeholder="Recipient Email"
                        :class="{
                          'text-negative-600':
                            !validRecipientEmail &&
                            newRecipient.email.length > 1
                        }"
                      />
                    </td>
                    <td class="text-right px-6 py-2">
                      <button
                        type="button"
                        class="text-sm text-primary-700 border border-primary-500 px-2 py-1 hover:bg-primary-100 hover:border-primary-700 hover:text-primary-800 transition-all transition-normal no-underline"
                        @click="addRecipient"
                        :disabled="!recipientValid || isDisabled"
                      >
                        &plus;
                        <span class="sr-only">Add Recipient</span>
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </form>
            <div class="flex justify-end px-6 py-4 border-t">
              <div class="w-24 mr-4">
                <app-button colour="close" @click="close">
                  Cancel
                </app-button>
              </div>
              <div class="w-24">
                <app-button
                  type="submit"
                  @click="doSave"
                  :disabled="isDisabled"
                >
                  Update
                </app-button>
              </div>
            </div>
          </app-card>
        </template>
      </app-modal>
    </portal>
  </div>
</template>

<script>
export default {
  name: "FundingAssessmentsRecipientsModal",
  props: {
    owner: {
      type: Object,
      required: true
    },
    recipients: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      newRecipient: {
        name: "",
        email: ""
      },
      customRecipients: [...this.recipients]
    };
  },
  computed: {
    isVisible() {
      return this.visible;
    },
    isNotVisible() {
      return !this.isVisible;
    },
    isDisabled() {
      return this.disabled || this.loading;
    },
    isNotDisabled() {
      return !this.isDisabled;
    },
    projectOwner() {
      return this.owner;
    },
    recipientValid() {
      return (
        this.validRecipientName &&
        this.validRecipientEmail &&
        !this.emailExists(this.newRecipient.email)
      );
    },
    validRecipientName() {
      return this.newRecipient.name.length > 0;
    },
    validRecipientEmail() {
      const re = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/;
      return re.test(this.newRecipient.email);
    },
    isDirty() {
      return this.customRecipients.length !== this.recipients.length;
    }
  },
  methods: {
    close() {
      if (this.isDirty) {
        if (
          confirm(
            "You have unsaved changes. Are you sure you want to close this window?"
          )
        ) {
          this.hide();
          this.reset();
        }
      } else {
        this.hide();
        this.reset();
      }
    },
    hide() {
      this.visible = false;
    },
    show() {
      this.visible = true;
    },
    reset() {
      this.newRecipient = {
        name: "",
        email: ""
      };
    },
    addRecipient() {
      if (this.recipientValid) {
        this.customRecipients.push({ ...this.newRecipient });
        this.$refs.nameInput.focus();
        this.reset();
      }
    },
    removeRecipient(index) {
      this.customRecipients.splice(index, 1);
    },
    emailExists(email) {
      if (email === this.projectOwner.email) {
        return true;
      }
      return this.customRecipients.some(recipient => recipient.email === email);
    },
    doSave() {
      if (this.isDisabled) {
        return;
      }

      if (this.recipientValid) {
        this.addRecipient();
      }

      this.$emit("save", this.customRecipients);
      this.hide();
      this.reset();
    }
  }
};
</script>
